import { useEffect, useState } from "react";
import { Settings as SettingsIcon, Brain, Trash2, Fingerprint } from "lucide-react";
import { api } from "@/lib/api";
import { registerPasskey, passkeysSupported } from "@/lib/passkeys";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function SettingsPage() {
  const { refresh } = useAuth();
  const [options, setOptions] = useState({ countries: [], currencies: [], languages: [] });
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/profile/options"), api.get("/profile")]).then(([o, p]) => {
      setOptions(o.data); setProfile(p.data);
    });
  }, []);
  if (!profile) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const update = (k, v) => setProfile((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile/preferences", {
        country: profile.country, preferred_currency: profile.preferred_currency,
        preferred_language_style: profile.preferred_language_style,
        main_goal: profile.main_goal, experience_level: profile.experience_level,
      });
      await refresh();
      toast.success("Preferences saved");
    } catch (e) {
      toast.error("Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-6 py-10 md:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Settings</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl"><SettingsIcon className="mr-1 inline h-6 w-6" /> Preferences</h1>

      <div className="mt-8 space-y-5 rounded-2xl border border-border bg-card p-6 md:p-8">
        <Field label="Country">
          <Select value={profile.country} onValueChange={(v) => update("country", v)}>
            <SelectTrigger className="h-11" data-testid="settings-country"><SelectValue /></SelectTrigger>
            <SelectContent>{options.countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Preferred currency">
          <Select value={profile.preferred_currency} onValueChange={(v) => update("preferred_currency", v)}>
            <SelectTrigger className="h-11" data-testid="settings-currency"><SelectValue /></SelectTrigger>
            <SelectContent>{options.currencies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Language style">
          <Select value={profile.preferred_language_style} onValueChange={(v) => update("preferred_language_style", v)}>
            <SelectTrigger className="h-11" data-testid="settings-lang"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="global_english">Global English</SelectItem>
              <SelectItem value="nigerian_english">Nigerian English</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Button onClick={save} disabled={saving} className="h-11 w-full" data-testid="settings-save">{saving ? "Saving…" : "Save preferences"}</Button>
      </div>

      <AiMemoryPanel />
      <PasskeyPanel />
    </div>
  );
}

function PasskeyPanel() {
  const { user } = useAuth();
  const [passkeys, setPasskeys] = useState([]);
  const [busy, setBusy] = useState(false);
  const supported = passkeysSupported();
  const load = () => api.get("/auth/passkeys").then(({ data }) => setPasskeys(data.passkeys || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    setBusy(true);
    try {
      await registerPasskey();
      if (user?.email) window.localStorage.setItem("zuntria_passkey_email", user.email);
      toast.success("Face ID / Fingerprint enabled on this device 🎉");
      load();
    } catch (err) {
      if (err?.name === "NotAllowedError") toast.error("Biometric prompt cancelled");
      else if (err?.name === "InvalidStateError") toast.error("This device already has a passkey registered");
      else toast.error(err?.response?.data?.detail || err?.message || "Could not enable biometric login");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this biometric login?")) return;
    try {
      await api.delete(`/auth/passkeys/${encodeURIComponent(id)}`);
      toast.success("Passkey removed");
      load();
    } catch { toast.error("Could not remove passkey"); }
  };

  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-6 md:p-8" data-testid="passkey-panel">
      <div className="flex items-center gap-2">
        <Fingerprint className="h-4 w-4 text-primary" />
        <h2 className="font-display text-lg">Face ID / Fingerprint login</h2>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Sign in instantly with your face or fingerprint — no password needed. Your biometrics never leave your device.
      </p>
      {!supported ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="passkey-unsupported">
          This browser doesn't support biometric login. Try Safari on iPhone or Chrome on Android.
        </p>
      ) : (
        <>
          <Button onClick={add} disabled={busy} className="mt-4 h-11 w-full sm:w-auto" data-testid="passkey-register-btn">
            <Fingerprint className="mr-2 h-4 w-4" />
            {busy ? "Waiting for biometrics…" : "Enable on this device"}
          </Button>
          {passkeys.length > 0 && (
            <ul className="mt-4 space-y-2">
              {passkeys.map((p) => (
                <li key={p.credential_id} className="flex items-center justify-between rounded-xl border border-border bg-background px-3 py-2.5">
                  <span>
                    <span className="block text-sm font-medium">{p.device_name || "Device"}</span>
                    <span className="block text-[11px] text-muted-foreground">
                      Added {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
                      {p.last_used_at ? ` · Last used ${new Date(p.last_used_at).toLocaleDateString()}` : ""}
                    </span>
                  </span>
                  <Button variant="outline" size="sm" onClick={() => remove(p.credential_id)} data-testid={`passkey-remove-${p.credential_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function AiMemoryPanel() {
  const [memory, setMemory] = useState(null);
  const [prefs, setPrefs] = useState({ memory_enabled: true, web_search_enabled: true });
  const load = () => api.get("/memory").then(({ data }) => setMemory(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/auth/me").then(({ data }) => setPrefs({
      memory_enabled: data.memory_enabled !== false,
      web_search_enabled: data.web_search_enabled !== false,
    })).catch(() => {});
  }, []);

  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      const { data } = await api.put("/memory/prefs", { [key]: next[key] });
      setPrefs(data);
      toast.success(key === "memory_enabled"
        ? (data.memory_enabled ? "Memory on — the AI will personalise your answers" : "Memory off — the AI only uses the current session")
        : (data.web_search_enabled ? "Internet search enabled" : "Internet search disabled"));
    } catch {
      setPrefs(prefs);
      toast.error("Could not save");
    }
  };

  const clear = async () => {
    if (!window.confirm("Clear everything the AI remembers about you?")) return;
    try {
      await api.delete("/memory");
      toast.success("AI memory cleared");
      load();
    } catch {
      toast.error("Could not clear memory");
    }
  };

  const p = memory?.profile || {};
  const facts = memory?.facts || [];
  const profileRows = [
    ["Goal", p.goal], ["Target", p.target], ["Industry", p.industry],
    ["Budget", p.budget], ["Country", p.country],
    ["Skills", Array.isArray(p.skills) ? p.skills.join(", ") : p.skills],
  ].filter(([, v]) => v);
  const empty = profileRows.length === 0 && facts.length === 0;

  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-6 md:p-8" data-testid="ai-memory-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg">What the AI remembers about you</h2>
        </div>
        {!empty && (
          <Button variant="outline" size="sm" onClick={clear} data-testid="clear-memory-btn">
            <Trash2 className="mr-1 h-3.5 w-3.5" /> Clear memory
          </Button>
        )}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Zuntria's AI learns your goals, budget and skills from your missions and chats to personalise every answer. You're in control — clear it any time.
      </p>
      <div className="mt-4 space-y-2">
        <ToggleRow
          label="AI memory"
          desc={prefs.memory_enabled ? "On — personalises answers from your goals & wins" : "Off — AI only uses the current session"}
          checked={prefs.memory_enabled}
          onChange={() => toggle("memory_enabled")}
          testId="memory-toggle"
        />
        <ToggleRow
          label="Internet search"
          desc={prefs.web_search_enabled ? "On — AI checks the live web for fresh answers with sources" : "Off — AI answers from its own knowledge only"}
          checked={prefs.web_search_enabled}
          onChange={() => toggle("web_search_enabled")}
          testId="web-search-toggle"
        />
      </div>
      {empty ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="memory-empty">
          Nothing yet — start a mission or chat with an assistant and the AI will start remembering.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          {profileRows.length > 0 && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {profileRows.map(([k, v]) => (
                <div key={k} className="rounded-xl border border-border bg-background p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k}</div>
                  <div className="mt-0.5 text-sm">{v}</div>
                </div>
              ))}
            </div>
          )}
          {facts.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Learned facts</div>
              <ul className="mt-1.5 space-y-1">
                {facts.slice().reverse().map((f, i) => (
                  <li key={i} className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs">{f.fact}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ToggleRow({ label, desc, checked, onChange, testId }) {
  return (
    <button onClick={onChange} data-testid={testId}
      className="flex w-full items-center justify-between rounded-xl border border-border bg-background px-3 py-2.5 text-left transition-colors hover:border-primary/40">
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-[11px] text-muted-foreground">{desc}</span>
      </span>
      <span className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-primary" : "bg-secondary"}`}>
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-[18px]" : "translate-x-0.5"}`} />
      </span>
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
